import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './exportMenu.css';
import { runLoading } from '../loading';
import { useNotify } from '../notifications';
import { showDialog } from '../confirm';

/** The ALPACA "Export to" format set, in the same order as legacy LIMS. */
type ExportKind = 'csv' | 'html' | 'image' | 'mht' | 'text' | 'pdf' | 'rtf' | 'xls' | 'xlsx' | 'docx';

/** Shared download helper (extracted so standalone callers can reuse it). */
async function downloadBlob(blob: Blob, name: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

/** Writes a Blob to an already-granted "Save As" file handle.
 *  NOTE (2026-09-23): merge 576e0371 took an older copy of this file and lost the
 *  code that CALLED this helper, so it compiled as an unused local (TS6133) and
 *  broke the build. Exported rather than deleted so the "Save As" path can be
 *  restored by whoever owns that merge — the helper itself is unchanged. */
export async function writeBlobToHandle(handle: FileSystemFileHandle, blob: Blob): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/**
 * Standalone XLSX / CSV exporter — use this from toolbar buttons that bypass
 * the "Export to ▾" dropdown (e.g. MSR's explicit "Export to XLSX" / "Export
 * to CSV" buttons matching legacy ALPACA).  Resolves all rows via `rowsProvider`
 * when supplied, otherwise falls back to the static `headers`/`rows`.
 * The CALLER is responsible for wrapping with `runLoading` and showing the
 * success/error notification.
 */
export async function triggerGridExport(
  kind: 'csv' | 'xlsx',
  filename: string,
  headers: string[],
  rows: string[][],
  rowsProvider?: () => Promise<{ headers: string[]; rows: string[][] }>,
): Promise<void> {
  const src = rowsProvider ? await rowsProvider() : { headers, rows };
  const H = src.headers, R = src.rows;
  if (kind === 'csv') {
    const csvCell = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const csv = [H, ...R].map((r) => r.map(csvCell).join(',')).join('\r\n');
    downloadBlob(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }), `${filename}.csv`);
  } else {
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([H, ...R]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Export');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  }
}

/** Extension/MIME/description per export kind, used by the native "Save As"
 *  picker (File System Access API) to suggest a filename and file-type filter. */
const SAVE_META: Record<ExportKind, { ext: string; mime: string; description: string }> = {
  csv:   { ext: '.csv',  mime: 'text/csv',                                                                 description: 'CSV File' },
  html:  { ext: '.html', mime: 'text/html',                                                                description: 'HTML File' },
  image: { ext: '.png',  mime: 'image/png',                                                                description: 'Image File' },
  mht:   { ext: '.mht',  mime: 'message/rfc822',                                                           description: 'MHT File' },
  text:  { ext: '.txt',  mime: 'text/plain',                                                                description: 'Text File' },
  pdf:   { ext: '.pdf',  mime: 'application/pdf',                                                          description: 'PDF File' },
  rtf:   { ext: '.rtf',  mime: 'application/rtf',                                                          description: 'RTF File' },
  xls:   { ext: '.xls',  mime: 'application/vnd.ms-excel',                                                 description: 'XLS File' },
  xlsx:  { ext: '.xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',        description: 'XLSX File' },
  docx:  { ext: '.docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',  description: 'DOCX File' },
};

const MENU: { kind: ExportKind; label: string; icon: string }[] = [
  { kind: 'csv',   label: 'CSV File',   icon: 'ti-file-type-csv' },
  { kind: 'html',  label: 'HTML File',  icon: 'ti-file-type-html' },
  { kind: 'image', label: 'Image File', icon: 'ti-photo' },
  { kind: 'mht',   label: 'MHT File',   icon: 'ti-world-code' },
  { kind: 'text',  label: 'Text File',  icon: 'ti-file-text' },
  { kind: 'pdf',   label: 'PDF File',   icon: 'ti-file-type-pdf' },
  { kind: 'rtf',   label: 'RTF File',   icon: 'ti-file-type-doc' },
  { kind: 'xls',   label: 'XLS File',   icon: 'ti-file-type-xls' },
  { kind: 'xlsx',  label: 'XLSX File',  icon: 'ti-file-type-xls' },
  { kind: 'docx',  label: 'DOCX File',  icon: 'ti-file-type-docx' },
];

/** Imperative handle — lets a parent (e.g. AppToolbar's Alt+X shortcut) open the
 *  dropdown programmatically. */
export interface ExportMenuHandle { open: () => void }

export interface ExportMenuProps {
  filename: string; headers: string[]; rows: string[][];
  /** Optional async source for ALL rows (e.g. a server-paginated list that must
   *  fetch every page for the export). When set, it is awaited at export time
   *  and its headers/rows are used instead of the static `headers`/`rows` (which
   *  then just seed the visible-page fallback). */
  rowsProvider?: () => Promise<{ headers: string[]; rows: string[][] }>;
  triggerClassName?: string;
  /** Render the trigger as a bare ICON (no "Export to ▾" caption) — for grid
   *  corners where legacy LIMS shows only the share glyph. Opt-in: every existing
   *  caller keeps the captioned button. */
  iconOnly?: boolean;
  /** Tabler icon class used when `iconOnly` is set. Defaults to the LIMS
   *  share/export glyph (tray with an arrow leaving the top). */
  icon?: string;
  /** Trigger tooltip (default "Export to") — lets the caller append the
   *  keyboard-shortcut hint, e.g. "Export to (Alt+X)". */
  title?: string;
}

/** Export-to dropdown for the accounting master pages — the full LIMS format set
 *  (CSV / HTML / Image / MHT / Text / PDF / RTF / XLS / XLSX / DOCX). Rows are the
 *  already-filtered, display-formatted string cells. */
export const ExportMenu = forwardRef<ExportMenuHandle, ExportMenuProps>(function ExportMenu(
  { filename, headers, rows, rowsProvider, triggerClassName = 'ca-btn', iconOnly = false, icon = 'ti-share-2', title = 'Export to' },
  fwdRef,
) {
  const [open, setOpen] = useState(false);
  /** Keyboard-cursor item index while the menu is open. */
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const notify = useNotify();
  useImperativeHandle(fwdRef, () => ({ open: () => setOpen(true) }), []);

  // The menu is PORTALLED to <body> and positioned fixed, so an ancestor with
  // `overflow: hidden` cannot clip it. That is the real failure this fixes: on the
  // SDMS detail the grid lives in `.dv-card { overflow: hidden }`, so the dropdown
  // was cut off at the card edge unless the card happened to be maximized
  // (client 2026-08-18). Same approach the shared column-filter popup uses.
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxH: number } | null>(null);

  /** Anchor under the trigger, flipping above when there is more room there, and
   *  capping the height so a long list scrolls instead of running off-screen. */
  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const GAP = 4, EDGE = 8;
    const m = menuRef.current?.getBoundingClientRect();
    const mw = m?.width || 180;
    const mh = m?.height || 320;
    const vw = window.innerWidth, vh = window.innerHeight;
    const below = vh - b.bottom - GAP - EDGE;
    const above = b.top - GAP - EDGE;
    let top: number, maxH: number;
    if (mh <= below || below >= above) { top = b.bottom + GAP; maxH = below; }
    else { maxH = above; top = b.top - GAP - Math.min(mh, above); }
    // FINAL VIEWPORT CLAMP — `below`/`above` measure space around the TRIGGER, and
    // the trigger itself can be scrolled out of view (the SDMS detail body scrolls,
    // so the bottom card's button can sit past the viewport edge). Without this the
    // menu was placed relative to an off-screen anchor and landed off-screen too.
    maxH = Math.max(120, Math.min(maxH, vh - EDGE * 2));
    const h = Math.min(mh, maxH);
    top = Math.max(EDGE, Math.min(top, vh - EDGE - h));
    // Right-aligned to the trigger (as the absolute version was), then clamped
    // so it never leaves the viewport on either edge.
    const left = Math.min(Math.max(EDGE, b.right - mw), Math.max(EDGE, vw - mw - EDGE));
    setPos({ top, left, maxH });
  }, []);

  useLayoutEffect(() => { if (!open) { setPos(null); return; } place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    // `true` = capture, so a scroll inside the grid re-anchors it too, not just
    // a window scroll.
    const h = () => place();
    window.addEventListener('scroll', h, true);
    window.addEventListener('resize', h);
    return () => { window.removeEventListener('scroll', h, true); window.removeEventListener('resize', h); };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    // The menu is no longer inside `ref`, so the outside-click test has to check
    // BOTH the trigger container and the portalled menu.
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  // Keyboard (mouse-free, ADR-0013, user report 2026-09-21: "the focus was not in the
  // popup — use keys to select"). Reset the cursor to the first item on open, and move
  // focus INTO the menu once it is placed (visible) so the grid behind loses its
  // focus ring. `activeRef` mirrors `active` for the window handler below.
  const activeRef = useRef(0);
  activeRef.current = active;
  useEffect(() => { if (open) setActive(0); }, [open]);
  useEffect(() => { if (open && pos) menuRef.current?.focus(); }, [open, pos]);
  useEffect(() => {
    if (!open || !pos) return;
    menuRef.current?.querySelector('.acc-export-item.active')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open, pos]);

  const csvCell = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const htmlEsc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const htmlTable = (hh: string[], rr: string[][]) =>
    `<table border="1" style="border-collapse:collapse;font:13px sans-serif">` +
    `<thead><tr>${hh.map((h) => `<th style="background:#e07b39;color:#fff;padding:4px 8px">${htmlEsc(h)}</th>`).join('')}</tr></thead>` +
    `<tbody>${rr.map((r) => `<tr>${r.map((c) => `<td style="padding:4px 8px">${htmlEsc(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;

  const run = async (kind: ExportKind) => {
    setOpen(false);
    const meta = SAVE_META[kind];

    // Direct browser download to the Downloads folder — no "Save As" picker
    // (user request 2026-09-22: "Export to download directly in the browser").
    // Every format streams straight to the browser's normal download, the same
    // as an HTTP deployment / Firefox / Safari already did.
    void meta;
    const download = downloadBlob;

    // Empty grid still exports (headers-only file) — Export to stays enabled, LIMS parity.
    const label = `Exporting to ${kind.toUpperCase()}…`;
    let exportedRows = 0;   // captured for the completion dialog's detail rows
    try {
    await runLoading((async () => {
      // Resolve the FULL rows: a server-paginated list supplies every row via
      // rowsProvider (fetched here, inside the loader); otherwise use the static
      // (already-filtered, client-side) headers/rows.
      const src = rowsProvider ? await rowsProvider() : { headers, rows };
      const H = src.headers, R = src.rows;
      exportedRows = R.length;
      switch (kind) {
        case 'csv': {
          const csv = [H, ...R].map((r) => r.map(csvCell).join(',')).join('\r\n');
          await download(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }), `${filename}.csv`);
          break;
        }
        case 'text': {
          const txt = [H, ...R].map((r) => r.join('\t')).join('\r\n');
          await download(new Blob([txt], { type: 'text/plain;charset=utf-8' }), `${filename}.txt`);
          break;
        }
        case 'html': {
          const html = `<!doctype html><html><head><meta charset="utf-8"><title>${htmlEsc(filename)}</title></head><body>${htmlTable(H, R)}</body></html>`;
          await download(new Blob([html], { type: 'text/html;charset=utf-8' }), `${filename}.html`);
          break;
        }
        case 'mht': {
          const html = `<html><head><meta charset="utf-8"></head><body>${htmlTable(H, R)}</body></html>`;
          const mht = `From: <Saved by Alpaca Intelligence>\r\nSubject: ${filename}\r\nMIME-Version: 1.0\r\nContent-Type: text/html; charset="utf-8"\r\n\r\n${html}`;
          await download(new Blob([mht], { type: 'message/rfc822' }), `${filename}.mht`);
          break;
        }
        case 'rtf': {
          const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/[{}]/g, (m) => '\\' + m);
          const body = [H, ...R].map((r) => r.map(esc).join('\\tab ')).join('\\par\r\n');
          await download(new Blob([`{\\rtf1\\ansi\\deff0 ${body}\\par}`], { type: 'application/rtf' }), `${filename}.rtf`);
          break;
        }
        case 'xls':
        case 'xlsx': {
          const XLSX = await import('xlsx');
          const ws = XLSX.utils.aoa_to_sheet([H, ...R]);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Export');
          const out = XLSX.write(wb, { bookType: kind === 'xls' ? 'biff8' : 'xlsx', type: 'array' }) as ArrayBuffer;
          await download(new Blob([out], { type: meta.mime }), `${filename}.${kind}`);
          break;
        }
        case 'pdf': {
          const { jsPDF } = await import('jspdf');
          await import('jspdf-autotable');
          const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          (doc as unknown as { autoTable: (o: unknown) => void }).autoTable({
            head: [H], body: R, styles: { fontSize: 9, cellPadding: 4 },
            headStyles: { fillColor: [224, 123, 57] }, margin: { top: 40, left: 20, right: 20 },
            didDrawPage: () => doc.text(filename, 20, 26),
          });
          await download(doc.output('blob'), `${filename}.pdf`);
          break;
        }
        case 'image': {
          const html2canvas = (await import('html2canvas')).default;
          const host = document.createElement('div');
          host.style.cssText = 'position:fixed;left:-99999px;top:0;background:#fff;padding:12px';
          host.innerHTML = htmlTable(H, R);
          document.body.appendChild(host);
          try {
            const canvas = await html2canvas(host, { backgroundColor: '#ffffff', scale: 2 });
            const blob: Blob | null = await new Promise((res) => canvas.toBlob(res, 'image/png'));
            if (blob) await download(blob, `${filename}.png`);
          } finally { host.remove(); }
          break;
        }
        case 'docx': {
          const { Document, Packer, Table, TableRow, TableCell, Paragraph, TextRun, WidthType } = await import('docx');
          const cell = (text: string, bold = false) =>
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: text ?? '', bold })] })] });
          const table = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({ children: H.map((h) => cell(h, true)) }),
              ...R.map((r) => new TableRow({ children: r.map((c) => cell(c)) })),
            ],
          });
          const doc = new Document({
            sections: [{ children: [
              new Paragraph({ children: [new TextRun({ text: filename, bold: true, size: 28 })] }),
              table,
            ] }],
          });
          await download(await Packer.toBlob(doc), `${filename}.docx`);
          break;
        }
      }
    })(), { blocking: true, label });
    // Post-operation acknowledgement (§18.6 — every operation confirms success).
    // Shown as the shared centred MODAL (BO UI Standard §13 Tier 1) rather than the
    // transient top banner: an export writes a FILE the user then has to go and open,
    // so the acknowledgement must be one they actively dismiss — it can't be allowed
    // to auto-dismiss/scroll away before they've seen which file was written. Uses
    // the milestone-success shape (detail rows) so the file name + row count are
    // visible. The failure path below already routes to the same modal (useNotify's
    // .error is auto-rerouted to errorDlg), so both outcomes now match. (2026-08-07)
    await showDialog({
      type: 'success',
      title: 'Export complete',
      message: `Exported to ${kind.toUpperCase()} successfully.`,
      rows: [
        { label: 'File', value: `${filename}${meta.ext}` },
        { label: 'Rows', value: exportedRows.toLocaleString() },
      ],
    });
    } catch {
      notify.error(`Export to ${kind.toUpperCase()} failed. Please try again.`);
    }
  };

  // Drive the menu from a WINDOW keydown in the CAPTURE phase while it is open, so
  // the arrows work even if focus never landed inside the portalled menu AND do NOT
  // leak to the grid behind it (capture + stopPropagation beats the grid row's own
  // ↑/↓ handler and AppShell's .content handler). ↑/↓ move the cursor (wrapping),
  // Home/End jump, Enter/Space run the cursor item, Esc closes → trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const n = MENU.length;
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === 'ArrowDown') { stop(); setActive(a => (a + 1) % n); }
      else if (e.key === 'ArrowUp') { stop(); setActive(a => (a - 1 + n) % n); }
      else if (e.key === 'Home') { stop(); setActive(0); }
      else if (e.key === 'End') { stop(); setActive(n - 1); }
      else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { stop(); const m = MENU[activeRef.current]; if (m) void run(m.kind); }
      else if (e.key === 'Escape') { stop(); setOpen(false); btnRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, run]);

  return (
    <div className="acc-export" ref={ref}>
      <button
        ref={btnRef}
        className={triggerClassName}
        title={title}
        aria-label="Export to"
        onClick={() => setOpen((o) => !o)}
      >
        {iconOnly ? <i className={`ti ${icon}`} /> : <><span className="tb-i tb-i-teal">📤</span> Export to ▾</>}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="acc-export-menu acc-export-menu-fixed"
          role="menu"
          aria-label="Export format"
          tabIndex={-1}
          style={pos ? { top: pos.top, left: pos.left, maxHeight: pos.maxH } : { visibility: 'hidden' }}
        >
          {MENU.map((m, i) => (
            <div key={m.kind} role="menuitem" tabIndex={-1}
              className={`acc-export-item${i === active ? ' active' : ''}`}
              onClick={() => run(m.kind)}
              onMouseEnter={() => setActive(i)}>
              <i className={`ti ${m.icon}`} /> {m.label}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
});
